import React, { useRef, useState, useEffect, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { apiClient } from "../App";
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Unlink,
  Undo,
  Redo,
  ImageIcon,
  Loader2,
  Code,
  Eye,
  Move
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";

const MenuBar = ({ editor, fileInputRef, uploading, onToggleHtml, isHtmlMode, onOpenResizeDialog }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("bold") ? "bg-slate-200" : ""}`}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("italic") ? "bg-slate-200" : ""}`}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("underline") ? "bg-slate-200" : ""}`}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("strike") ? "bg-slate-200" : ""}`}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("bulletList") ? "bg-slate-200" : ""}`}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("orderedList") ? "bg-slate-200" : ""}`}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: "left" }) ? "bg-slate-200" : ""}`}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: "center" }) ? "bg-slate-200" : ""}`}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: "right" }) ? "bg-slate-200" : ""}`}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={setLink}
        disabled={isHtmlMode}
        className={`h-8 w-8 p-0 ${editor.isActive("link") ? "bg-slate-200" : ""}`}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link") || isHtmlMode}
        className="h-8 w-8 p-0"
        title="Remove Link"
      >
        <Unlink className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={triggerFileUpload}
        disabled={uploading || isHtmlMode}
        className="h-8 px-2 gap-1"
        title="Upload Image"
        data-testid="upload-image-btn"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ImageIcon className="w-4 h-4" />
        )}
        <span className="text-xs">Image</span>
      </Button>

      {/* Resize Image Button - always visible, opens dialog to select image */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onOpenResizeDialog}
        disabled={isHtmlMode}
        className="h-8 px-2 gap-1"
        title="Resize Images"
        data-testid="resize-image-btn"
      >
        <Move className="w-4 h-4" />
        <span className="text-xs">Resize</span>
      </Button>

      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo() || isHtmlMode}
        className="h-8 w-8 p-0"
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo() || isHtmlMode}
        className="h-8 w-8 p-0"
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </Button>

      <div className="flex-1" />

      {/* HTML Edit Toggle */}
      <Button
        type="button"
        variant={isHtmlMode ? "default" : "ghost"}
        size="sm"
        onClick={onToggleHtml}
        className={`h-8 px-2 gap-1 ${isHtmlMode ? "bg-slate-900 text-white" : ""}`}
        title={isHtmlMode ? "Switch to Visual Editor" : "Edit HTML"}
        data-testid="toggle-html-btn"
      >
        {isHtmlMode ? <Eye className="w-4 h-4" /> : <Code className="w-4 h-4" />}
        <span className="text-xs">{isHtmlMode ? "Visual" : "HTML"}</span>
      </Button>
    </div>
  );
};

// Image Resize Dialog Component
const ImageResizeDialog = ({ open, onClose, images, onResize }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(300);
  const [aspectRatio, setAspectRatio] = useState(1.33);

  // Update dimensions when selecting a different image
  useEffect(() => {
    if (open && images.length > 0 && images[selectedIndex]) {
      const img = images[selectedIndex];
      // Load natural dimensions
      const imgEl = new window.Image();
      imgEl.onload = () => {
        const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
        setAspectRatio(ratio);
        // Use current width/height or natural
        const currentWidth = img.width || Math.min(imgEl.naturalWidth, 600);
        setWidth(currentWidth);
        setHeight(Math.round(currentWidth / ratio));
      };
      imgEl.src = img.src;
    }
  }, [open, selectedIndex, images]);

  const handleWidthChange = (newWidth) => {
    const w = parseInt(newWidth) || 0;
    setWidth(w);
    setHeight(Math.round(w / aspectRatio));
  };

  const handleHeightChange = (newHeight) => {
    const h = parseInt(newHeight) || 0;
    setHeight(h);
    setWidth(Math.round(h * aspectRatio));
  };

  const handlePresetSize = (preset) => {
    let newWidth;
    switch (preset) {
      case 'small': newWidth = 150; break;
      case 'medium': newWidth = 300; break;
      case 'large': newWidth = 500; break;
      case 'full': newWidth = 700; break;
      default: newWidth = 300;
    }
    setWidth(newWidth);
    setHeight(Math.round(newWidth / aspectRatio));
  };

  const handleApply = () => {
    if (images[selectedIndex]) {
      onResize(selectedIndex, width, height);
    }
    onClose();
  };

  if (!images || images.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="w-5 h-5" />
              Resize Images
            </DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center text-slate-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No images found in this template.</p>
            <p className="text-sm mt-1">Upload an image first using the "Image" button.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const currentImage = images[selectedIndex];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="w-5 h-5" />
            Resize Image {images.length > 1 ? `(${selectedIndex + 1} of ${images.length})` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Image Selector (if multiple images) */}
          {images.length > 1 && (
            <div>
              <Label className="text-sm text-slate-600 mb-2 block">Select Image</Label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={img.src || `img-${idx}`}
                    onClick={() => setSelectedIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 border-2 rounded overflow-hidden ${
                      idx === selectedIndex ? 'border-blue-500' : 'border-slate-200'
                    }`}
                  >
                    <img src={img.src} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="border rounded-md p-3 bg-slate-50 flex items-center justify-center min-h-[120px]">
            <img 
              src={currentImage?.src} 
              alt="Preview" 
              style={{ 
                width: Math.min(width, 400), 
                height: 'auto',
                maxHeight: '150px', 
                objectFit: 'contain' 
              }}
            />
          </div>

          {/* Preset Sizes */}
          <div>
            <Label className="text-sm text-slate-600 mb-2 block">Quick Sizes</Label>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" variant="outline" size="sm" onClick={() => handlePresetSize('small')}>
                Small (150px)
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => handlePresetSize('medium')}>
                Medium (300px)
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => handlePresetSize('large')}>
                Large (500px)
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => handlePresetSize('full')}>
                Full (700px)
              </Button>
            </div>
          </div>

          {/* Custom Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="img-width" className="text-sm">Width (px)</Label>
              <Input
                id="img-width"
                type="number"
                value={width}
                onChange={(e) => handleWidthChange(e.target.value)}
                min={50}
                max={1200}
                className="mt-1"
                data-testid="image-width-input"
              />
            </div>
            <div>
              <Label htmlFor="img-height" className="text-sm">Height (px)</Label>
              <Input
                id="img-height"
                type="number"
                value={height}
                onChange={(e) => handleHeightChange(e.target.value)}
                min={50}
                max={1200}
                className="mt-1"
                data-testid="image-height-input"
              />
            </div>
          </div>

          {/* Info */}
          <p className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
            💡 Aspect ratio is locked. Changing width automatically adjusts height and vice versa.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleApply} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-resize-btn">
            Apply Size
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RichTextEditor = ({ value, onChange, placeholder }) => {
  const fileInputRef = useRef(null);
  const editorContainerRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [resizeDialogOpen, setResizeDialogOpen] = useState(false);
  const [editorImages, setEditorImages] = useState([]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color,
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: "editor-image",
        },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      if (!isHtmlMode) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[200px] p-4 focus:outline-none",
      },
    },
  });

  // Update editor content when value prop changes externally
  useEffect(() => {
    if (editor && !isHtmlMode && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor, isHtmlMode]);

  // Extract images from editor content
  const extractImagesFromEditor = useCallback(() => {
    if (!editor) return [];
    
    const images = [];
    const html = editor.getHTML();
    
    // Parse the HTML to find all images
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgElements = doc.querySelectorAll('img');
    
    imgElements.forEach((img, index) => {
      images.push({
        src: img.getAttribute('src') || '',
        width: parseInt(img.getAttribute('width')) || null,
        height: parseInt(img.getAttribute('height')) || null,
        index
      });
    });
    
    return images;
  }, [editor]);

  // Open resize dialog
  const handleOpenResizeDialog = () => {
    const images = extractImagesFromEditor();
    setEditorImages(images);
    setResizeDialogOpen(true);
  };

  // Apply resize to specific image
  const handleResizeImage = (imageIndex, newWidth, newHeight) => {
    if (!editor) return;
    
    // Get current HTML
    let html = editor.getHTML();
    
    // Parse and modify the specific image
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgElements = doc.querySelectorAll('img');
    
    if (imgElements[imageIndex]) {
      imgElements[imageIndex].setAttribute('width', newWidth.toString());
      imgElements[imageIndex].setAttribute('height', newHeight.toString());
      imgElements[imageIndex].style.width = `${newWidth}px`;
      imgElements[imageIndex].style.height = `${newHeight}px`;
      
      // Get the updated HTML
      const newHtml = doc.body.innerHTML;
      
      // Update editor
      editor.commands.setContent(newHtml);
      onChange(newHtml);
      
      toast.success(`Image resized to ${newWidth}×${newHeight}px`);
    }
  };

  // Toggle HTML mode
  const handleToggleHtml = () => {
    if (isHtmlMode) {
      // Switching from HTML to Visual - apply HTML changes
      if (editor) {
        editor.commands.setContent(htmlContent);
        onChange(htmlContent);
      }
    } else {
      // Switching from Visual to HTML - get current HTML
      if (editor) {
        setHtmlContent(editor.getHTML());
      }
    }
    setIsHtmlMode(!isHtmlMode);
  };

  // Handle HTML textarea change
  const handleHtmlChange = (e) => {
    setHtmlContent(e.target.value);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { url } = response.data;
      
      // Insert the image into the editor
      if (editor && url) {
        editor.chain().focus().setImage({ src: url }).run();
        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white" ref={editorContainerRef}>
      <MenuBar 
        editor={editor} 
        fileInputRef={fileInputRef} 
        uploading={uploading}
        onToggleHtml={handleToggleHtml}
        isHtmlMode={isHtmlMode}
        onOpenResizeDialog={handleOpenResizeDialog}
      />
      
      {isHtmlMode ? (
        <div className="relative">
          <textarea
            value={htmlContent}
            onChange={handleHtmlChange}
            className="w-full min-h-[200px] p-4 font-mono text-sm bg-slate-900 text-green-400 focus:outline-none resize-y"
            placeholder="Enter HTML code..."
            spellCheck={false}
            data-testid="html-editor-textarea"
          />
          <div className="absolute top-2 right-2 bg-slate-800 text-slate-400 text-xs px-2 py-1 rounded">
            HTML Mode
          </div>
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileUpload}
        className="hidden"
        data-testid="image-file-input"
      />

      {/* Image Resize Dialog */}
      <ImageResizeDialog
        open={resizeDialogOpen}
        onClose={() => setResizeDialogOpen(false)}
        images={editorImages}
        onResize={handleResizeImage}
      />
      
      <style>{`
        .ProseMirror img.editor-image {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          margin: 8px 0;
          cursor: pointer;
        }
        .ProseMirror img.editor-image:hover {
          outline: 2px solid #93c5fd;
          outline-offset: 2px;
        }
        .ProseMirror img.ProseMirror-selectednode {
          outline: 3px solid #3b82f6;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
};
